import { DropdownMenu, IconButton } from '@hotjar/hotsauce';
import { useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface EntityActionsDropdownProps {
  placement?: 'bottom-end' | 'bottom-start';
  onDelete?: () => void;
  onCopyLink?: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onRename?: () => void;
}

export function EntityActionsDropdown({
  placement = 'bottom-start',
  onDelete,
  onCopyLink,
  onDuplicate,
  onEdit,
  onRename,
}: EntityActionsDropdownProps): JSX.Element {
  const menuRef = useRef<HTMLInputElement | null>(null);
  const restoreFocus = useCallback(() => {
    if (!menuRef.current) return;
    menuRef.current.focus();
  }, [menuRef]);
  const { t } = useTranslation('feedback');

  const handleEdit = useCallback(() => {
    onEdit?.();
    restoreFocus();
  }, [onEdit, restoreFocus]);

  const handleOnCopyLink = useCallback(() => {
    onCopyLink?.();
    restoreFocus();
  }, [onCopyLink, restoreFocus]);

  const handleDuplicate = useCallback(() => {
    onDuplicate?.();
    restoreFocus();
  }, [onDuplicate, restoreFocus]);

  const handleDelete = useCallback(() => {
    onDelete?.();
    restoreFocus();
  }, [onDelete, restoreFocus]);

  const handleRename = useCallback(() => {
    onRename?.();
    restoreFocus();
  }, [onRename, restoreFocus]);

  const actions = useMemo(() => {
    function* getActions() {
      if (onEdit)
        yield (
          <DropdownMenu.Item
            key="edit"
            icon="pencil"
            title={t('EntityActions.Menu.edit')}
            onClick={handleEdit}
          >
            {t('EntityActions.Menu.edit')}
          </DropdownMenu.Item>
        );
      if (onRename)
        yield (
          <DropdownMenu.Item
            key="rename"
            icon="text_field_short"
            title={t('EntityActions.Menu.rename')}
            onClick={handleRename}
          >
            {t('EntityActions.Menu.rename')}
          </DropdownMenu.Item>
        );
      if (onCopyLink)
        yield (
          <DropdownMenu.Item
            key="copy"
            icon="hyperlink"
            title={t('EntityActions.Menu.copy-link')}
            onClick={handleOnCopyLink}
          >
            {t('EntityActions.Menu.copy-link')}
          </DropdownMenu.Item>
        );
      if (onDuplicate)
        yield (
          <DropdownMenu.Item
            key="duplicate"
            icon="duplicate"
            title={t('EntityActions.Menu.duplicate')}
            hasDivider
            onClick={handleDuplicate}
          >
            {t('EntityActions.Menu.duplicate')}
          </DropdownMenu.Item>
        );
      if (onDelete)
        yield (
          <DropdownMenu.Item
            key="delete"
            icon="trash"
            title={t('EntityActions.Menu.delete')}
            onClick={handleDelete}
            kind="danger_ghost"
            hasDivider
          >
            {t('EntityActions.Menu.delete')}
          </DropdownMenu.Item>
        );
    }
    return [...getActions()];
  }, [
    onEdit,
    handleEdit,
    onCopyLink,
    handleOnCopyLink,
    onDuplicate,
    handleDuplicate,
    onDelete,
    handleDelete,
    onRename,
    handleRename,
    t,
  ]);

  return (
    <DropdownMenu
      ref={menuRef}
      placement={placement}
      size="s"
      /* appendTo parent so the focus handling is kept within the parent menu item */
      appendTo="parent"
      trigger={
        <IconButton
          icon="menu_kebab"
          kind="ghost"
          size="s"
          description={t('EntityActions.Menu.actions.title')}
          tooltipText={t('EntityActions.Menu.actions.tooltip')}
        />
      }
    >
      {actions}
    </DropdownMenu>
  );
}
